import React, { useCallback, useContext, useEffect, useState } from "react";
import i18next from "i18next";
import _ from "lodash";
import { useFormContext, useWatch, useFieldArray } from "react-hook-form";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import useStyles from "style/js-style/containers/administration/calculator/CalculatorDialogStyle";
import EAIcon from "components/common/EAIcon";
import {
  CalculatorContext,
  TargetFieldCategory,
  getAvailableOperatorOptions,
  getTargetFieldOptionsForDocument,
  getTargetFieldOptionsForDossier,
  getTargetFieldOptionsForSourceVariable,
  getTargetFieldOptionsForThirdParty,
  parseTargetFieldCategoryFromVariable,
  parseTargetFieldFromVariable,
  getCriterionModel,
  parseDocumentTypeIdFromVariable,
  CalculationDeclarationContext,
  VariableType,
  getFlatSubFieldsOptions,
  parseTargetInnerFieldFromVariable,
  getTargetFieldOptionsForContextModels,
  ContextDirectField,
  getTargetFieldOptionsForContextDirectField,
  parseDossierTypeIdFromVariable,
  isMatchingDossierTarget,
  isMatchingThirdPartyTarget,
  parseTargetComplexListSubFieldFromVariable,
} from 'services/common/CalculatorUtils';
import CalculatorSelectInput from 'containers/administration/calculator/form/calculatorInputs/CalculatorSelectInput';
import {
  AttributeType,
  DocumentTypePropertyType,
  FieldDisplayVariant,
  FieldType,
  Operator,
} from "services/common/Constants";
import CriterionValues from "./CriterionValues";
import { Box, List, ListItem, } from "@mui/material";
import EAColorIcon from '../../../../../components/common/EAColorIcon';


const Criteria = ({
  fieldName,
  onCriteriaOrderChange,
  readOnly = false
}) => {
  const { classes } = useStyles();
  const formMethods = useFormContext();

  const {
    control
  } = formMethods;

  const {
    addCallback,
    removeCallBack,
    isValidVariable,
  } = useContext(CalculationDeclarationContext);

  const { fields: criteria, append, remove, move } = useFieldArray({
    control,
    name: `${fieldName}`,
  });

  const onAddEntry = useCallback(() => {
    append(getCriterionModel());
  }, [append]);

  const onRemoveEntry = (id, index) => {
    remove(index);
    removeCallBack(id);
  };

  const handleDragEnd = useCallback((result) => {
    if (result?.destination) {
      move(result.source.index, result.destination.index);
      onCriteriaOrderChange();
    }
  }, [move, onCriteriaOrderChange]);

  return (
    <>
      <div className={classes.branchCriteriaWrapper}>
        <DragDropContext onDragEnd={!readOnly && handleDragEnd}>
          <Droppable droppableId="droppable" direction="vertical">
            {(provided) => (
              <List
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {criteria?.map((item, index) => {
                  return (
                    <Draggable key={item.id} draggableId={`${item.id}`} index={index}>
                      {(provided) => (
                        <ListItem
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              m: 0,
                              p: 2,
                              bgcolor: "rgba(242, 243, 247, 0.3)",
                              border: '1px solid',
                              borderColor: 'grey.300',
                              borderRadius: 1,
                              alignItems: 'center'
                            }}>
                            <Box sx={{
                              width: '5%',
                              color: '#C6CBD9'
                            }}>
                              <EAIcon icon={"drag-indicator"} />
                            </Box>
                            <Box sx={{
                              width: '5%',
                              color: '#F4B63E',
                              fontSize: '20px',
                              fontWeight: 600,
                              lineHeight: '24px',
                              letterSpacing: '0.15000000596046448px',
                              textAlign: 'left',
                            }}>
                              {`${index + 1}`}
                            </Box>
                            <Box sx={{ width: '90%' }}>
                              <Criterion
                                data={item}
                                fieldName={`${fieldName}.${index}`}
                                onRemove={() => onRemoveEntry(item.id, index)}
                                addCallBack={(callBack) => addCallback(item.id, callBack)}
                                isValidVariable={isValidVariable}
                                readOnly={readOnly}
                              />
                            </Box>
                          </Box>
                        </ListItem>
                      )
                      }
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </div >

      {/* Add a criterion */}
      {!readOnly &&
        <div
          className={classes.addCriterion}
          style={{ padding: "0 0 16px 24px", width: "180px", cursor: "pointer", display: "flex", alignItems: 'center' }
          }
          onClick={() => onAddEntry()}
        >
          <EAColorIcon icon={"add_green"} width={20} svgStyle={{ position: "relative", top: "2px" }} />
          <span> {i18next.t("calculator.dialog.create.addCriterion")}</span>
        </div >
      }
    </>
  );
}

const Criterion = ({ fieldName, onRemove, addCallBack, isValidVariable, readOnly = false }) => {
  const { classes } = useStyles();
  const [showLabel, setShowLabel] = useState(true);

  const {
    countries,
    activities,
    thirdpartyAttributes,
    dossierAttributes,
    documentTypes,
    calculatedAttributes,
    thirdPartyIndicators,
    financialStrengthScores,
    financialStrengthRatings,
    sourceData,
    sourceVariables,
    showInduedScores,
    showSourceData,
    showTransparencyScore,
    showFinancialStrengthScores,
    showEsgScores, 
    enableTransparencyScore,
    showEcovadisScores,
    showSecurityRatingScores,
    showCreditsafeScores,
    lang,
    contextModels,
    collectiveProceedingSteps
  } = useContext(CalculatorContext);

  const formMethods = useFormContext();
  const {
    setValue,
    getValues,
    clearErrors,
  } = formMethods;

  const [definedVariables, setDefinedVariables] = useState([]);
  const [availableVariableOptions, setAvailableVariableOptions] = useState();
  const [availableOperatorOptions, setAvailableOperatorOptions] = useState();
  const [availableValueOptions, setAvailableValueOptions] = useState([]);
  const [variantAutoComplete, setVariantAutoComplete] = useState(false);

  const [selectedFieldType, setSelectedFieldType] = useState();
  const [selectedFieldSubType, setSelectedFieldSubType] = useState('');
  const [selectedComplexListSubFieldType, setSelectedComplexListSubFieldType] = useState();
  const [selectedOperator, setSelectedOperator] = useState();

  let variables = useWatch({ name: `variables` });
  let currentCriterion = useWatch({ name: `${fieldName}` });


  useEffect(() => {
    const filteredVariables = _.filter(
      variables,
      (variable) => {
        return isValidVariable(variable) && VariableType.RESULT_FORMULA_ARGUMENT !== variable?.type
      }
    );

    setDefinedVariables(filteredVariables);

    setAvailableVariableOptions(
      filteredVariables.map((entry) => ({
        value: entry.name,
        label: entry.name,
      })).sort((a, b) => {
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
      })
    );
  }, [variables, isValidVariable]);

  const getAvailableTargetFields = useCallback((variable) => {
    let availableTargetFields = [];
    switch (parseTargetFieldCategoryFromVariable(variable)) {
      case TargetFieldCategory.THIRD_PARTY:
        availableTargetFields = getFlatSubFieldsOptions(getTargetFieldOptionsForThirdParty(
          countries,
          activities,
          thirdpartyAttributes,
          _.filter(calculatedAttributes, item => isMatchingThirdPartyTarget(item)),
          _.filter(thirdPartyIndicators, item => isMatchingThirdPartyTarget(item)),
          financialStrengthScores,
          financialStrengthRatings,
          sourceData,
          showInduedScores,
          showTransparencyScore,
          showFinancialStrengthScores,
          showEsgScores,
          showSourceData,
          enableTransparencyScore,
          variable?.type === VariableType.RESULT_FORMULA_ARGUMENT,
          showEcovadisScores,
          showSecurityRatingScores,
          showCreditsafeScores,
          lang,
          collectiveProceedingSteps
        ));
        break;

      case TargetFieldCategory.DOSSIER:
        availableTargetFields = getFlatSubFieldsOptions(getTargetFieldOptionsForDossier(
          dossierAttributes,
          _.filter(calculatedAttributes, item => isMatchingDossierTarget(item, parseDossierTypeIdFromVariable(variable))),
          _.filter(thirdPartyIndicators, item => isMatchingDossierTarget(item, parseDossierTypeIdFromVariable(variable))),
          _.filter(sourceVariables, item => isMatchingDossierTarget(item, parseDossierTypeIdFromVariable(variable)))
        ));
        break;

      case TargetFieldCategory.DOCUMENT:
        availableTargetFields = getFlatSubFieldsOptions(getTargetFieldOptionsForDocument(
          _.find(documentTypes, { value: parseDocumentTypeIdFromVariable(variable) })?.properties || [],
          _.find(documentTypes, { value: parseDocumentTypeIdFromVariable(variable) })?.ocrExtractedFields || [],
        ));
        break;

      case TargetFieldCategory.SOURCE_VARIABLE:
        availableTargetFields = getFlatSubFieldsOptions(getTargetFieldOptionsForSourceVariable(
          _.filter(sourceVariables, item => isMatchingThirdPartyTarget(item))
        ));
        break;
      case TargetFieldCategory.CONTEXT:
        const targetFieldData = variable.targetField.target_field_data.data_for_context;
        const targetFieldNature = targetFieldData.target_field_nature;
        if(ContextDirectField.includes(targetFieldNature)) {
          availableTargetFields = [getTargetFieldOptionsForContextDirectField(targetFieldNature)];
        } else {
          availableTargetFields = getFlatSubFieldsOptions(getTargetFieldOptionsForContextModels(
            contextModels,
            targetFieldData
          ));
        }
        break;
      default:
        break;
    }

    return availableTargetFields;
  }, [
    countries,
    activities,
    thirdpartyAttributes,
    dossierAttributes,
    documentTypes,
    calculatedAttributes,
    thirdPartyIndicators,
    financialStrengthScores,
    financialStrengthRatings,
    sourceData,
    sourceVariables,
    showInduedScores,
    showTransparencyScore,
    showSourceData,
    showFinancialStrengthScores,
    showEsgScores,
    enableTransparencyScore,
    showEcovadisScores,
    showSecurityRatingScores,
    showCreditsafeScores,
    lang,
    contextModels,
    collectiveProceedingSteps
  ]);

  const getTargetFieldEntryFromVariable = useCallback((variable) => {
    if (!variable) return undefined;
    const field = parseTargetFieldFromVariable(variable);
    const innerField = parseTargetInnerFieldFromVariable(variable);
    const targetFields = getAvailableTargetFields(variable);
    const targetComplexListSubField = parseTargetComplexListSubFieldFromVariable(variable);

    const targetField = _.find(targetFields, { value: field });

    if (innerField) {
      const targetInnerField = targetField?.subFields?.find(subField => subField.value === innerField);
      return {
        value: innerField,
        type: targetInnerField?.type,
        subType: targetInnerField?.subType,
        valueOptions: targetInnerField?.valueOptions || [],
        optionsDisplayVariant: targetInnerField?.optionsDisplayVariant
      }
    } else if (targetComplexListSubField) {
      const targetSubField = targetField?.subFields?.find(subField => subField.label === targetComplexListSubField.field);
      if (targetSubField) {
        return {
          value: targetSubField.value,
          type: targetSubField.type,
          subType: targetSubField.subType,
          complexListSubField: {
            value: targetComplexListSubField.field,
            type: targetComplexListSubField.field_type
          },
          valueOptions: targetSubField?.valueOptions || [],
          optionsDisplayVariant: targetSubField?.optionsDisplayVariant
        }
      }
    } else {
      return _.find(targetFields, { value: field });
    }
  }, [getAvailableTargetFields]);

  // Change l'opérateur et reinitialise les valeurs
  const onOperatorChange = useCallback((newValue) => {
    setSelectedOperator(newValue);

    switch (newValue) {
      case Operator.IN_SET:
      case Operator.NOT_IN_SET:
      case Operator.IS_NULL:
      case Operator.NOT_NULL:
        setValue(`${fieldName}.values`, []);
        break;

      case Operator.EQUAL:
      case Operator.CONTAINS:
        const variable = _.find(definedVariables, { id: currentCriterion?.variableId });
        const targetFieldEntry = getTargetFieldEntryFromVariable(variable);

        if (
          (targetFieldEntry?.type === FieldType.ATTRIBUTE &&
            targetFieldEntry?.subType === AttributeType.LIST_MULTIPLE) ||
          (targetFieldEntry?.type === FieldType.FORM_FIELD &&
            targetFieldEntry?.subType === DocumentTypePropertyType.CASE_A_COCHER)
        ) {
          setValue(`${fieldName}.values`, []);
        } else {
          setValue(`${fieldName}.values`, [""]);
        }
        break;

      default:
        setValue(`${fieldName}.values`, [""]);
        break;
    }

    clearErrors(`${fieldName}.values`);
  }, [fieldName, setValue, definedVariables, getTargetFieldEntryFromVariable, currentCriterion, clearErrors]);

  const onVariableNameChange = useCallback((newValue) => {
    const variable = _.find(definedVariables, { name: newValue });

    if (variable !== undefined) {
      const targetFieldEntry = getTargetFieldEntryFromVariable(variable);

      setValue(`${fieldName}.variableId`, variable.id);
      setValue(`${fieldName}.variableType`, variable.type);

      setSelectedFieldType(targetFieldEntry?.type);
      setSelectedFieldSubType(targetFieldEntry?.subType || ``);
      setSelectedComplexListSubFieldType(targetFieldEntry?.complexListSubField?.type);

      const filteredOperatorOptions = getAvailableOperatorOptions(targetFieldEntry);
      setAvailableOperatorOptions(filteredOperatorOptions);

      if (filteredOperatorOptions?.length > 0) {
        setValue(`${fieldName}.operator`, filteredOperatorOptions[0].value);
        onOperatorChange(filteredOperatorOptions[0].value);
      } else {
        setValue(`${fieldName}.operator`, "");
      }
      clearErrors(`${fieldName}.operator`);

      //Criterion values
      setAvailableValueOptions(targetFieldEntry?.valueOptions);
      setVariantAutoComplete(targetFieldEntry?.optionsDisplayVariant && targetFieldEntry?.optionsDisplayVariant === FieldDisplayVariant.AUTOCOMPLETE ? true : false)

      //Clear all previous errors that might have been raised on 'values' field
      clearErrors(`${fieldName}.values`);
    }
  }, [fieldName, definedVariables, getTargetFieldEntryFromVariable, setValue, clearErrors, onOperatorChange]);

  const onVariableDelete = useCallback((variable) => {
    const fieldValue = getValues(fieldName);
    // If the Criterion corresponds to the deleted variable we clear it.
    if (fieldValue?.variableName === variable?.name) {
      setValue(fieldName, {
        groupId: fieldValue.groupId,
        id: fieldValue.id,
      })
      setAvailableOperatorOptions()
    }
  }, [getValues, setValue, fieldName]);

  const onVariableUpdate = useCallback((variable, action, value = null) => {

    if (variable.id === currentCriterion?.variableId) {
      if(action?.includes("intermediatefield")) {
        action = "field";
      }

      switch (action) {
        case "name":
          setValue(`${fieldName}.variableName`, value);
          break;
        case "target_field_category":
        case "target_field_nature":
        case "field":
        case "document_type_id":
        case "dossier_type_id":
          onVariableNameChange(variable.name);
          break;
        case "delete":
          onVariableDelete(variable);
          break;
        default:
          break;
      }
    }

  }, [currentCriterion, fieldName, onVariableNameChange, onVariableDelete, setValue])

  // Quand l'id de la variable cible est modifié, ajoute ou remplace une callBack
  useEffect(() => {
    if (currentCriterion?.variableId) {
      addCallBack(onVariableUpdate);
    }
    // eslint-disable-next-line
  }, [currentCriterion, onVariableUpdate])

  // Récupère les opérateurs et valeurs des critères à la restitution ou modification des variables
  useEffect(() => {
    const variable = _.find(definedVariables, { id: currentCriterion?.variableId });
    const targetFieldEntry = getTargetFieldEntryFromVariable(variable);

    if (targetFieldEntry !== undefined){
      // MAJ type
      setSelectedFieldType(targetFieldEntry?.type);
      setSelectedFieldSubType(targetFieldEntry?.subType || ``);
      setSelectedComplexListSubFieldType(targetFieldEntry?.complexListSubField?.type);
      // MAJ opérateur
      const filteredOperatorOptions = getAvailableOperatorOptions(targetFieldEntry);
      setAvailableOperatorOptions(filteredOperatorOptions);
      // MAJ valeurs
      setAvailableValueOptions(targetFieldEntry?.valueOptions);
      setVariantAutoComplete(targetFieldEntry.optionsDisplayVariant && targetFieldEntry.optionsDisplayVariant === FieldDisplayVariant.AUTOCOMPLETE
        ? true : false)
    }
  }, [currentCriterion, definedVariables, getTargetFieldEntryFromVariable, selectedFieldType])

  useEffect(() => {
    const variable = _.find(definedVariables, { name: currentCriterion?.variableName });
    const targetFieldEntry = getTargetFieldEntryFromVariable(variable);
    setSelectedFieldType(targetFieldEntry?.type);
    setSelectedFieldSubType(targetFieldEntry?.subType || ``);
    setSelectedComplexListSubFieldType(targetFieldEntry?.complexListSubField?.type);

    setSelectedOperator(currentCriterion?.operator);
  }, [currentCriterion, definedVariables, getTargetFieldEntryFromVariable]);

  useEffect(() => {
    setShowLabel(true);
    // setShowLabel(parseInt(fieldName?.slice(-1) || `0`) === 0);
  }, [fieldName]);

  return (
    <div className={classes.variableBranchLineRoot}>
      {!readOnly &&
        <EAIcon
          icon={"highlight_off"}
          className={classes.variableBranchLineCrossIcon}
          style={{ top: "45px" }}
          onClick={() => onRemove()}
        />
      }

      {/* Variable */}
      {availableVariableOptions && (
        <div className={classes.variableLineNameItem}>
          <CalculatorSelectInput
            fieldName={`${fieldName}.variableName`}
            isRequired
            label={showLabel ? i18next.t("calculator.dialog.create.label.variable") : ``}
            placeholder={i18next.t(
              `calculator.dialog.create.placeholder.variableName`
            )}
            options={availableVariableOptions}
            onChange={(newValue) => {
              onVariableNameChange(newValue);
            }}
            validate={() => {
              return null;
            }}
            variant="standard"
            labelVariant="chip"
            className={classes.inputVariableNameBranch}
            dt
            disabled={readOnly}
          />
        </div>
      )}

      {/* Opérateur */}
      {availableOperatorOptions && (
        <div className={classes.variableLineNameItem} style={{ width: "140px" }}>
          <CalculatorSelectInput
            fieldName={`${fieldName}.operator`}
            label={showLabel ? i18next.t(`calculator.dialog.create.label.operator`) : ``}
            isRequired
            placeholder={i18next.t(
              `calculator.dialog.create.placeholder.selectOperator`
            )}
            options={availableOperatorOptions}
            onChange={(newValue) => {
              onOperatorChange(newValue);
            }}
            validate={() => {
              return null;
            }}
            variant="standard"
            labelVariant="chip"
            className={classes.inputVariableNameBranch}
            disabled={readOnly}
          />
        </div>
      )}

      {/* Valeurs */}
      <div className={classes.variableLineNameItem}>
        <CriterionValues
          fieldName={`${fieldName}.values`}
          showLabel={showLabel}
          selectedFieldType={selectedFieldType}
          selectedFieldSubType={selectedFieldSubType}
          selectedComplexListSubFieldType={selectedComplexListSubFieldType}
          selectedOperator={selectedOperator}
          availableValueOptions={availableValueOptions}
          variantAutoComplete={variantAutoComplete}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}

export default Criteria;
